import {
  getStoredFbclid,
  getStoredUtmCampaign,
  getStoredUtmSource,
} from "@/components/feature/utm-source-capture";
import { type AgeGroup, getAgeGroupFromStorage } from "@/lib/age-group/store";
import { getStoredPaidAdChannel } from "@/lib/analytics/paid-ad";

export const NPS_EVENT_SURVEY_SHOWN = "nps_survey_shown";
export const NPS_EVENT_SCORE_SUBMITTED = "nps_score_submitted";
export const NPS_EVENT_SURVEY_COMPLETED = "nps_survey_completed";
export const NPS_EVENT_SURVEY_DISMISSED = "nps_survey_dismissed";

export type NpsAgeGroup = AgeGroup | "unknown";

/** Where the visitor left the sticky survey via the X control. */
export type NpsDismissStage = "before_score" | "after_score";

type CaptureFn = (event: string, properties?: Record<string, unknown>) => void;

export type NpsAnalyticsContext = {
  age_group: NpsAgeGroup;
  game_slug: string;
  utm_source: string | null;
  utm_campaign: string | null;
  paid_ad_channel: "meta" | null;
  has_fbclid: boolean;
};

export function resolveNpsAgeGroup(
  ageGroup: AgeGroup | null = getAgeGroupFromStorage()
): NpsAgeGroup {
  return ageGroup ?? "unknown";
}

/** Shared attribution + age props for NPS PostHog events. */
export function buildNpsAnalyticsContext(gameSlug: string): NpsAnalyticsContext {
  const utmSource = getStoredUtmSource();
  const utmCampaign = getStoredUtmCampaign();
  const paidAdChannel = getStoredPaidAdChannel();
  return {
    age_group: resolveNpsAgeGroup(),
    game_slug: gameSlug,
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    paid_ad_channel: paidAdChannel,
    has_fbclid: Boolean(getStoredFbclid()),
  };
}

function capture(
  captureFn: CaptureFn | undefined,
  event: string,
  properties: Record<string, unknown>
) {
  if (!captureFn) return;
  try {
    captureFn(event, properties);
  } catch (error) {
    console.error("Error capturing NPS analytics event", { event, error });
  }
}

export function captureNpsSurveyShown(captureFn: CaptureFn | undefined, gameSlug: string): void {
  capture(captureFn, NPS_EVENT_SURVEY_SHOWN, buildNpsAnalyticsContext(gameSlug));
}

export function captureNpsScoreSubmitted(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  score: number
): void {
  capture(captureFn, NPS_EVENT_SCORE_SUBMITTED, {
    ...buildNpsAnalyticsContext(gameSlug),
    score,
  });
}

export function captureNpsSurveyCompleted(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  extras: { score: number; has_feedback: boolean }
): void {
  capture(captureFn, NPS_EVENT_SURVEY_COMPLETED, {
    ...buildNpsAnalyticsContext(gameSlug),
    score: extras.score,
    has_feedback: extras.has_feedback,
  });
}

export function captureNpsSurveyDismissed(
  captureFn: CaptureFn | undefined,
  gameSlug: string,
  dismissStage: NpsDismissStage,
  extras?: { score: number | null }
): void {
  capture(captureFn, NPS_EVENT_SURVEY_DISMISSED, {
    ...buildNpsAnalyticsContext(gameSlug),
    dismiss_stage: dismissStage,
    score: extras?.score ?? null,
  });
}
